import { Schema } from "@effect/schema";
import {
  ADDRESS_ZERO,
  Address,
  AssetKey,
  GraphAddress,
  MarketAssetMeta,
  MarketMeta,
  ProdProtocolKeyService,
  getChainKey,
  getChainKeyFromProtocol,
  getIsWrappedNative,
  getProtocol,
} from "@silo/types";
import { Array, Effect, pipe } from "effect";
import { GraphClient } from "../../graph-client";
import { GraphPercent } from "../../graph-client/graph-percent";
import { ContractsClient } from "../../contracts-client";

export type SourceKvMarketMeta = ReturnType<typeof sourceKvMarketMeta>;
export type SourceKvMarketMetaSuccess =
  Effect.Effect.Success<SourceKvMarketMeta>;
export type SourceKvMarketMetaError = Effect.Effect.Error<SourceKvMarketMeta>;
export type SourceKvMarketMetaContext =
  Effect.Effect.Context<SourceKvMarketMeta>;

export function sourceKvMarketMeta() {
  return Effect.gen(function* () {
    const [{ silos: graphMarketArray }, routerAddress] = yield* Effect.all(
      [
        GraphClient.request({
          query,
          schema: MarketMetaArrayResponse,
        }),
        ContractsClient.getRouterAddress(),
      ],
      { concurrency: "unbounded" }
    );

    const marketMetaArray: MarketMeta[] = yield* Effect.all(
      graphMarketArray.map((graphMarket) =>
        mapToMarketMeta({ graphMarket, routerAddress })
      ),
      { concurrency: "unbounded" }
    );

    return marketMetaArray;
  });
}

const query = `#graphql
  query MarketMetaArray {
    silos {
      id
      archived
      marketAssets: market {
        inputToken {
          id
          symbol
          name
          decimals
          activeOracle {
            oracle {
              id
            }
          }
        }
        sToken {
          id
        }
        spToken {
          id
        }
        dToken {
          id
        }
        maximumLTV
        liquidationThreshold
      }
      baseAsset {
        id
      }
      bridgeAsset(orderBy: lastPriceUSD, orderDirection: desc) {
        id
      }
    }
  }
`;

export type MarketMetaArrayResponse = typeof MarketMetaArrayResponse.Type;
export type MarketMetaArrayResponseEncoded =
  typeof MarketMetaArrayResponse.Encoded;

type GraphMarket = MarketMetaArrayResponse["silos"][number];

type GraphMarketAsset = GraphMarket["marketAssets"][number];

export const MarketMetaArrayResponse = Schema.Struct({
  silos: Schema.Array(
    Schema.Struct({
      id: GraphAddress,
      archived: Schema.Boolean,
      marketAssets: Schema.Array(
        Schema.Struct({
          inputToken: Schema.Struct({
            id: GraphAddress,
            symbol: Schema.String,
            name: Schema.String,
            decimals: Schema.Number,
            activeOracle: Schema.NullOr(
              Schema.Struct({
                oracle: Schema.Struct({ id: GraphAddress }),
              })
            ),
          }),
          sToken: Schema.Struct({
            id: GraphAddress,
          }),
          spToken: Schema.Struct({
            id: GraphAddress,
          }),
          dToken: Schema.Struct({
            id: GraphAddress,
          }),
          maximumLTV: GraphPercent,
          liquidationThreshold: GraphPercent,
        })
      ),
      baseAsset: Schema.Struct({
        id: GraphAddress,
      }),
      bridgeAsset: Schema.Tuple(
        Schema.Struct({
          id: GraphAddress,
        }),
        Schema.optionalElement(
          Schema.Struct({
            id: GraphAddress,
          })
        )
      ),
    })
  ),
}).annotations({ identifier: "MarketMetaArrayResponse" });

interface MapToMarketMetaParams {
  graphMarket: GraphMarket;
  routerAddress: Address;
}

function mapToMarketMeta({
  graphMarket,
  routerAddress,
}: MapToMarketMetaParams) {
  return Effect.gen(function* () {
    const protocolKey = yield* ProdProtocolKeyService;

    const marketAddress = graphMarket.id;
    const { marketAssets, archived } = graphMarket;

    const baseAsset = yield* pipe(
      marketAssets,
      Array.findFirst(
        (asset) => asset.inputToken.id === graphMarket.baseAsset.id
      ),
      Effect.andThen((graphMarketAsset) =>
        mapToMarketAssetMeta({
          graphMarketAsset,
          marketAddress,
          assetKey: "base",
        })
      )
    );

    const firstBridgeAsset = yield* pipe(
      graphMarket.bridgeAsset[0].id,
      (id) =>
        Array.findFirst(marketAssets, (asset) => asset.inputToken.id === id),
      Effect.andThen((graphMarketAsset) =>
        mapToMarketAssetMeta({
          graphMarketAsset,
          marketAddress,
          assetKey: "firstBridge",
        })
      )
    );

    const secondBridgeAssetId = graphMarket.bridgeAsset[1]?.id;

    const secondBridgeAsset = yield* secondBridgeAssetId
      ? pipe(
          Array.findFirst(
            marketAssets,
            (asset) => asset.inputToken.id === secondBridgeAssetId
          ),
          Effect.andThen((graphMarketAsset) =>
            mapToMarketAssetMeta({
              graphMarketAsset,
              marketAddress,
              assetKey: "secondBridge",
            })
          )
        )
      : Effect.succeed(undefined);

    const chainKey = getChainKeyFromProtocol(protocolKey);

    const marketMeta: MarketMeta = {
      protocolKey,
      chainKey,
      marketAddress,
      baseAsset,
      bridgeAssets: [
        firstBridgeAsset,
        ...(secondBridgeAsset ? ([secondBridgeAsset] as const) : ([] as const)),
      ],
      isDeprecated: archived,
    };

    return marketMeta;
  });
}

interface MapToMarketAssetMetaParams {
  marketAddress: Address;
  graphMarketAsset: GraphMarketAsset;
  assetKey: AssetKey;
}

function mapToMarketAssetMeta({
  marketAddress,
  graphMarketAsset,
  assetKey,
}: MapToMarketAssetMetaParams) {
  return Effect.gen(function* () {
    const protocolKey = yield* ProdProtocolKeyService;

    const chainId = pipe(
      protocolKey,
      getProtocol,
      (protocol) => protocol.chain.id
    );

    const {
      inputToken,
      sToken,
      spToken,
      dToken,
      maximumLTV,
      liquidationThreshold,
    } = graphMarketAsset;

    const { decimals } = inputToken;
    const tokenAddress = inputToken.id;
    const rawSymbol = inputToken.symbol;
    const rawName = inputToken.name;
    const chainKey = getChainKey(chainId);
    const isNative = getIsWrappedNative(chainKey)(tokenAddress);
    const priceProvider = inputToken.activeOracle?.oracle.id ?? ADDRESS_ZERO;

    const marketAssetMeta: MarketAssetMeta = {
      marketAddress,
      protocolKey,
      chainKey,
      tokenAddress,
      assetKey,
      rawSymbol,
      rawName,
      decimals,
      isNative,
      priceProvider,
      collateralShareToken: sToken.id,
      protectedShareToken: spToken.id,
      debtShareToken: dToken.id,
      maxLtv: maximumLTV,
      lt: liquidationThreshold,
    };

    return marketAssetMeta;
  });
}
